import { Button, Grid, ScrollArea, Stack, Text, Title } from '@mantine/core';
import { modals } from '@mantine/modals';
import { useEffect, useMemo } from 'react';

import { Transit, TransitGroup } from '@e-origin/shared';

import { DateTimePickerInput, LeavePageNotification, MultiLangCodes, StickyContainer, TextInput } from '@components';
import { TRANSIT_SECURITY_GRID_COL_ARRIVAL, TRANSIT_SECURITY_GRID_COL_DEPARTURE } from '@constants';
import { useCodeListSystem } from '@hooks/useCodeListSystem';
import { getAmendmentType } from '../../utils';
import { TransitDuplicateRow } from '../TransitDuplicateRow';
import { TransitCustomerSection } from './TransitCustomerSection';
import { TransitGuarantees } from './TransitGuarantees';
import { TransitLocationSection } from './TransitLocationSection';
import { TransitPlaceSection } from './TransitPlaceSection';
import { TransitTransportSection } from './TransitTransportSection';
import { useTransitViewGeneralInfoFormInitializer } from './useTransitViewGeneralInfoFormInitializer';
import { getPresentationDateError } from './validateTransitViewGeneralInfoForm';

interface TransitViewGeneralInfoProps {
  transit: Transit;
  editDisabled: boolean;
  editDisabledConditionalForArrival: boolean;
}

export const TransitViewGeneralInfo: React.FC<TransitViewGeneralInfoProps> = ({ transit, editDisabled, editDisabledConditionalForArrival }) => {
  const isArrival = transit.generalInfo?.group === TransitGroup.ARRIVAL;
  const codelistSystems = useCodeListSystem({ isTransit: true });

  const { isGeneralAmendment, isGuaranteeAmendment } = getAmendmentType(transit.TransitOperation.amendment);

  const openOverwriteMessage = () => {
    modals.openConfirmModal({
      title: 'Confirm save',
      children: (
        <Text>There are some headers values that had been defined. Saving will overwrite the lower level values. Are you sure you want to continue?</Text>
      ),
      labels: { confirm: 'Continue', cancel: 'Cancel' },
      onConfirm: handleOverwriteConfirm,
    });
  };

  const { form, onSubmit, buildFormValues } = useTransitViewGeneralInfoFormInitializer({
    transit,
    openOverwriteMessage,
  });

  const handleOverwriteConfirm = () => onSubmit(form.values, true);

  const { additionalDeclarationType, presentationOfTheGoodsDateAndTime } = form.values.TransitOperation;
  const presentationDateError = useMemo(
    () => getPresentationDateError(additionalDeclarationType, presentationOfTheGoodsDateAndTime),
    [additionalDeclarationType, presentationOfTheGoodsDateAndTime],
  );

  useEffect(() => {
    form.setValues(buildFormValues(transit));
    form.resetDirty({});
  }, [transit]);

  return (
    <>
      <LeavePageNotification hasUnsavedChanges={form.isDirty()} />

      <ScrollArea h="100%" offsetScrollbars="y">
        <form onSubmit={form.onSubmit((values) => onSubmit(values, false))}>
          <Stack mx="xl">
            <Stack mb="xl">
              <Title order={5} fw={600}>
                General
              </Title>
              <Grid>
                {!isArrival && (
                  <Grid.Col span={2}>
                    <TextInput {...form.getInputProps('Consignment.referenceNumberUCR')} label="Reference number/UCR" disabled={editDisabled} />
                  </Grid.Col>
                )}
                {!isArrival && (
                  <Grid.Col span={2}>
                    <MultiLangCodes
                      {...form.getInputProps('TransitOperation.additionalDeclarationType')}
                      label="Additional declaration"
                      system={codelistSystems}
                      code="CL042"
                      disabled={editDisabled || isGeneralAmendment}
                    />
                  </Grid.Col>
                )}
                <Grid.Col span={isArrival ? TRANSIT_SECURITY_GRID_COL_ARRIVAL : TRANSIT_SECURITY_GRID_COL_DEPARTURE}>
                  <MultiLangCodes
                    {...form.getInputProps('TransitOperation.security')}
                    label="Security"
                    system={codelistSystems}
                    code="DeclarationTypeSecurity"
                    disabled={editDisabled || isGeneralAmendment || isGuaranteeAmendment}
                  />
                </Grid.Col>
                {!isArrival && (
                  <Grid.Col span={4}>
                    <MultiLangCodes
                      {...form.getInputProps('TransitOperation.specificCircumstanceIndicator')}
                      label="Specific circumstance indicator"
                      system={codelistSystems}
                      code="SpecificCircumstanceIndicatorCo"
                      disabled={editDisabled}
                    />
                  </Grid.Col>
                )}
                {!isArrival && (
                  <Grid.Col span={2}>
                    <MultiLangCodes
                      {...form.getInputProps('TransitOperation.bindingItinerary')}
                      label="Binding itinerary"
                      system={codelistSystems}
                      code="CL027"
                      disabled={editDisabled}
                    />
                  </Grid.Col>
                )}
              </Grid>

              <Grid>
                {!isArrival && (
                  <>
                    <Grid.Col span={2.5}>
                      <MultiLangCodes
                        {...form.getInputProps('CustomsOfficeOfDeparture.referenceNumber')}
                        label="Customs office of departure"
                        system={codelistSystems}
                        code="CustomsOfficeDeparture"
                        disabled={editDisabled || isGeneralAmendment}
                      />
                    </Grid.Col>
                    <Grid.Col span={2.5}>
                      <MultiLangCodes
                        {...form.getInputProps('CustomsOfficeOfDestinationDeclared.referenceNumber')}
                        label="Customs office of destination"
                        system={codelistSystems}
                        code="CustomsOfficeDestination"
                        disabled={editDisabled}
                      />
                    </Grid.Col>
                  </>
                )}
                {isArrival && (
                  <Grid.Col span={3}>
                    <MultiLangCodes
                      {...form.getInputProps('CustomsOfficeOfDestinationActual.referenceNumber')}
                      label="Customs office of destination actual"
                      system={codelistSystems}
                      code="CustomsOfficeDestination"
                      disabled={editDisabled}
                    />
                  </Grid.Col>
                )}
                <Grid.Col span={2.5}>
                  <MultiLangCodes
                    {...form.getInputProps('Consignment.countryOfDestination')}
                    label="Country of destination"
                    system={codelistSystems}
                    code="CountryCodesCommonTransit"
                    disabled={editDisabled}
                  />
                </Grid.Col>
                {!isArrival && (
                  <Grid.Col span={2.5}>
                    <MultiLangCodes
                      {...form.getInputProps('Consignment.countryOfDispatch')}
                      label="Country of dispatch"
                      system={codelistSystems}
                      code="CountryCodesCommonTransit"
                      disabled={editDisabled}
                    />
                  </Grid.Col>
                )}
                <Grid.Col span={2}>
                  <TextInput {...form.getInputProps('Consignment.grossMass')} label="Gross mass" disabled={editDisabledConditionalForArrival} />
                </Grid.Col>
              </Grid>

              {!isArrival && (
                <Grid>
                  <Grid.Col span={3}>
                    <DateTimePickerInput
                      {...form.getInputProps('TransitOperation.presentationOfTheGoodsDateAndTime')}
                      error={presentationDateError}
                      label="Presentation of the goods date and time"
                      disabled={editDisabled}
                      clearable
                      data-pw="presentation-of-the-goods-date-and-time-input"
                    />
                  </Grid.Col>
                </Grid>
              )}
            </Stack>

            <TransitCustomerSection form={form} editDisabled={editDisabled || isGuaranteeAmendment} />

            {!isArrival && <TransitGuarantees form={form} disabled={editDisabled && !isGuaranteeAmendment} />}

            <Stack mb="xl">
              <Title order={5} fw={600}>
                Authorisations
              </Title>
              <TransitDuplicateRow
                addButtonLabel="Add"
                addButtonDataPW="add-authorisations-btn"
                form={form}
                inputs={[
                  {
                    type: 'MultiLang',
                    label: 'Authorisation Type',
                    name: 'type',
                    width: 20,
                    code: isArrival ? 'AuthorisationTypeDestination' : 'AuthorisationTypeDeparture',
                    excludeList: form.getInputProps('Authorisation').value?.map((a) => a.type),
                  },
                  {
                    type: 'Input',
                    name: 'referenceNumber',
                    label: 'Authorization referenceNumber',
                    width: 30,
                  },
                  {
                    type: 'DatePicker',
                    name: 'limitDate',
                    inputName: 'TransitOperation.limitDate',
                    label: 'TransitOperation limitDate',
                    width: 30,
                    displayCondition: (row) => row.type === 'C521',
                  },
                ]}
                arrayPath="Authorisation"
                disabled={isGuaranteeAmendment || editDisabled}
                maxRows={5}
                cleanupAdditionalData={(row) => {
                  if (row.type === 'C521') {
                    form.setFieldValue('TransitOperation.limitDate', '');
                  }
                }}
              />
            </Stack>

            <TransitLocationSection form={form} editDisabled={editDisabled || isGuaranteeAmendment} />
            <TransitPlaceSection form={form} editDisabled={editDisabled || isGuaranteeAmendment} isArrival={isArrival} />
            <TransitTransportSection
              form={form}
              editDisabled={editDisabled || isGuaranteeAmendment}
              editDisabledConditionalForArrival={editDisabledConditionalForArrival}
              isArrival={isArrival}
            />
          </Stack>

          <StickyContainer>
            <Button
              type="submit"
              disabled={(editDisabled && editDisabledConditionalForArrival) || !form.isValid()}
              variant="filled"
              size="lg"
              data-pw="save-transit-btn"
            >
              Save
            </Button>
          </StickyContainer>
        </form>
      </ScrollArea>
    </>
  );
};
